import { Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { lastValueFrom, Subject, switchMap, takeUntil, filter, take } from 'rxjs';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { Option } from 'src/app/base/models/option.model';
import { IrisoService } from '../../services/iriso.service';
import { StockSummaryService } from '../../services/stock-summary.service';
import { CommonService } from '../../services/common.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { InterCorpIrisoResponseData } from '../../models/iriso.model';
import { DetailCondition, StockDetail } from '../../models/stock-summary-master.model';

interface ItemOption extends Option {
  description?: string;
  brand?: string;
}

@Component({
  selector: 'app-inter-corp-ir-iso',
  templateUrl: './inter-corp-ir-iso.component.html',
  styleUrls: ['./inter-corp-ir-iso.component.scss'],
})
export class InterCorpIrIsoComponent extends SimpleSearchComponent {
  override rowDataKeyName: string = 'seq';
  override rowDataDeleteHintFieldName: string = 'seq';
  override componentClassName: string = 'InterCorpIrIsoComponent';

  override showActions: boolean = false;
  override showNoDataFoundMessage: boolean = false;

  // OU (Corp No.) AutoComplete
  ouSuggestions: Option[] = [];
  private readonly ouSubject$ = new Subject<string | undefined>();

  // Brand AutoComplete
  brandSuggestions: Option[] = [];
  private readonly brandSubject$ = new Subject<string | undefined>();

  // Item Number AutoComplete
  itemSuggestions: ItemOption[] = [];
  private readonly itemSubject$ = new Subject<string | undefined>();

  // Vendor Category Options
  vendorCategoryOptions: Option[] = [];
  loadingVendorCtg: boolean = false;

  queryId: string = '';
  totalQty: number = 0;

  isMobile: boolean = false;

  private resolvedInventoryItemId: number = 0;
  private resolvedOuOption: Option | null = null;
  private resolvedVendorCtg: string = '';

  constructor(
    private readonly irisoService: IrisoService,
    private readonly stockSummaryService: StockSummaryService,
    private readonly commonService: CommonService,
    private readonly accountService: AccountService,
    private readonly authService: AuthService
  ) {
    super();
  }

  override initFormGroup(): void {
    this.formGroup = this.formBuilder.nonNullable.group({
      ouId: ['', Validators.required],
      itemNumber: ['', Validators.required],
      itemDesc: [{ value: '', disabled: true }],
      brand: [''],
      vendorCategory: [''],
    });
  }

  override initTableColumns(): void {
    this.tableColumns = [
      { header: 'field.seq', field: 'seq', sortable: true },
      { header: 'field.ir-no', field: 'irNo', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.status', field: 'status', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.ou', field: 'ou', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.ou-name', field: 'ouName', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.line', field: 'line', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.item-number', field: 'item', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.source-wh-org', field: 'sourceWHOrg', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.source-organization', field: 'sourceOrg', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.dest-wh-org', field: 'destWHOrg', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.dest-organization', field: 'destOrg', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.qty', field: 'qty', sortable: true, numberField: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.esd', field: 'esd', sortable: true, dateField: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.so', field: 'so', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.so-line-no', field: 'soLineNo', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.sales-id', field: 'sales', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.sale-req', field: 'salesName', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.cust-po-number', field: 'custPoNumber', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.cust-number', field: 'customerNumber', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
      { header: 'field.cust-name', field: 'customerName', sortable: true, filter: { type: 'text', matchMode: 'contains' } },
    ];
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doQuery(): Promise<boolean> {
    this.isMobile = this.checkIfMobile();
    const ouValue = this.formControl('ouId').value;
    const itemValue = this.formControl('itemNumber').value;
    const brandValue = this.formControl('brand').value;
    const vendorCategoryValue = this.formControl('vendorCategory').value;

    const ouId = ouValue?.id ? Number(ouValue.id) : 0;
    const parsedItemId = Number(itemValue?.id);
    const inventoryItemId = (itemValue?.id != null && !isNaN(parsedItemId)) ? parsedItemId : 0;
    const brand: string = brandValue?.value ?? '';
    const category: string = vendorCategoryValue ?? '';
    const empNo: string = this.authService.getUser()?.username ?? '';
    const language = this.translateService.currentLang?.startsWith('zh') ? 'ZHT' : 'US';
    const uuid = crypto.randomUUID();

    try {
      const res = await lastValueFrom(
        this.irisoService.queryInterCorpIriso({
          ouId,
          empNo,
          inventoryItemId,
          language,
          category: category || undefined,
          brand: brand || undefined,
          uuid,
        })
      );
      this.queryId = res.queryId ?? '';
      this.detailDatas = this.mapResponseData(res.responseData ?? []);
      this.totalQty = res.total?.quantity ?? 0;
    } catch {
      this.detailDatas = [];
      this.totalQty = 0;
    }
    return true;
  }

  private mapResponseData(data: InterCorpIrisoResponseData[]): any[] {
    return data.map((item, index) => ({
      seq: index + 1,
      irNo: item.prNo,
      status: item.status,
      ou: item.orgCode,
      ouName: item.orgName,
      line: item.lineNum,
      item: item.itemNumber,
      sourceWHOrg: item.sourceOrgCode,
      sourceOrg: item.sourceOrgName,
      destWHOrg: item.destOrgCode,
      destOrg: item.destOrgName,
      qty: item.quantity,
      esd: item.needByDate,
      so: item.so,
      soLineNo: item.supplierSoLineNo,
      sales: item.salesCode,
      salesName: item.salesrepName,
      custPoNumber: item.custPoNumber,
      customerNumber: item.customerNumber,
      customerName: item.customerName,
    }));
  }

  private checkIfMobile(): boolean {
    return window.innerWidth <= 768;
  }

  override async initFormData(): Promise<any> {
    this.isMobile = this.checkIfMobile();
    const data = this.dynamicDialogConfig.data;
    if (data?.ouId && !data?.ou) {
      const orgs = await lastValueFrom(
        this.stockSummaryService.getOrgs(String(data.ouId)).pipe(take(1))
      ).catch(() => []);
      this.resolvedOuOption = orgs[0] ?? null;
    } else if (!data?.ou && !data?.ouId) {
      const orgs = await lastValueFrom(this.stockSummaryService.getOrgs().pipe(take(1))).catch(() => []);
      this.resolvedOuOption = orgs[0] ?? null;
    }
    if (data?.itemNumber) {
      const items = await lastValueFrom(
        this.commonService.getItemsWithDesc(data.itemNumber, data.inventoryItemId).pipe(take(1))
      ).catch(() => []);
      const match = items.find((i: any) => i.value === data.itemNumber || i.label === data.itemNumber);
      if (match) {
        this.resolvedInventoryItemId = match.id ? Number(match.id) : 0;
        if (match.description) {
          this.formGroup.patchValue({ itemDesc: match.description });
        }
      }
    }
    if (data?.inventoryItemId || this.resolvedInventoryItemId) {
      const itemId = this.resolvedInventoryItemId || data.inventoryItemId;
      const ouId = data?.ouId ? Number(data.ouId) : (data?.ou?.id ? Number(data.ou.id) : 0);
      const detailCondition = new DetailCondition(
        { ouId, inventoryItemId: itemId, itemNumber: data?.itemNumber, vendorItem: '', brand: data?.brand } as any,
        crypto.randomUUID()
      );
      const detailRes = await lastValueFrom(
        this.stockSummaryService.queryStockSummaryMasterDetailDatas(detailCondition).pipe(take(1))
      ).catch(() => null);
      if (detailRes && !detailRes.failure && detailRes.responseData) {
        const detail = detailRes.responseData as StockDetail;
        this.resolvedVendorCtg = detail.vendorCtg ?? '';
      }
    }
    return true;
  }

  override afterFormDataLoadSuccess(): void {
    const data = this.dynamicDialogConfig.data;
    if (!data) return;

    const patches: any = {};
    if (data.ou) {
      patches.ouId = data.ou;
    } else if (this.resolvedOuOption) {
      patches.ouId = this.resolvedOuOption;
    }
    if (data.brand) {
      patches.brand = { id: data.brand, value: data.brand, label: data.brand };
    }
    if (data.itemNumber) {
      patches.itemNumber = {
        id: this.resolvedInventoryItemId || data.inventoryItemId || data.itemNumber,
        value: data.itemNumber,
        label: data.itemNumber,
      };
    }

    this.formGroup.patchValue(patches);

    this.changeDetectorRef.detectChanges();

    const hasOu = data.ou || this.resolvedOuOption;
    const shouldAutoQuery = !!(hasOu && data.itemNumber);

    if (this.resolvedVendorCtg) {
      const vendorCtg = this.normalizeVendorCtg(this.resolvedVendorCtg);
      const brand: string = patches.brand?.value ?? data?.brand ?? '';
      if (brand) {
        this.loadVendorCategories(brand, () => {
          this.formGroup.patchValue({ vendorCategory: vendorCtg });
          if (shouldAutoQuery) { setTimeout(() => this.query()); }
        });
        return;
      } else {
        this.formGroup.patchValue({ vendorCategory: vendorCtg });
      }
    }

    if (shouldAutoQuery) { setTimeout(() => this.query()); }
  }

  // --- AutoComplete search methods ---

  searchOuSuggestions(event: { query: any }): void {
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => this.stockSummaryService.getOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.ouSuggestions = data;
        });
    }
    this.ouSubject$.next(event.query);
  }

  searchBrandSuggestions(event: { query: any }): void {
    if (!this.brandSubject$.observed) {
      this.brandSubject$
        .pipe(
          switchMap((keyword) => this.accountService.getBrands(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.brandSuggestions = data;
        });
    }
    this.brandSubject$.next(event.query);
  }

  searchItemSuggestions(event: { query: any }): void {
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => this.commonService.getItemsWithDesc(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => { this.itemSuggestions = data; },
          error: () => { this.itemSuggestions = []; },
        });
    }
    this.itemSubject$.next(event.query);
  }

  onItemSelect(event: { value: ItemOption }): void {
    const selected = event.value;
    if (selected?.description) {
      this.formGroup.patchValue({ itemDesc: selected.description });
    } else {
      this.formGroup.patchValue({ itemDesc: '' });
    }
    const currentBrand: string = this.formControl('brand').value?.value ?? '';
    const newBrand: string = selected?.brand ?? '';
    if (selected?.brand) {
      const brand = selected.brand;
      this.formGroup.patchValue({ brand: { id: brand, value: brand, label: brand } });
    } else {
      this.formGroup.patchValue({ brand: null });
    }
    // Always reset ctg when item changes; clear options only if brand also changed
    this.formGroup.patchValue({ vendorCategory: null });
    if (currentBrand !== newBrand) {
      this.vendorCategoryOptions = [];
    }
    // Load vendor ctg for the newly selected item
    const inventoryItemId = selected?.id ? Number(selected.id) : 0;
    if (inventoryItemId && newBrand) {
      const ouValue = this.formControl('ouId').value;
      const ouId = ouValue?.id ? Number(ouValue.id) : 0;
      const detailCondition = new DetailCondition(
        { ouId, inventoryItemId, itemNumber: selected.value, vendorItem: '', brand: newBrand } as any,
        crypto.randomUUID()
      );
      this.stockSummaryService.queryStockSummaryMasterDetailDatas(detailCondition)
        .pipe(take(1), takeUntil(this._destroying$))
        .subscribe({
          next: (detailRes) => {
            if (detailRes && !detailRes.failure && detailRes.responseData) {
              const detail = detailRes.responseData as StockDetail;
              const vendorCtg = detail.vendorCtg ? this.normalizeVendorCtg(detail.vendorCtg) : '';
              if (vendorCtg) {
                if (this.vendorCategoryOptions.length === 0) {
                  this.loadVendorCategories(newBrand, () => {
                    this.formGroup.patchValue({ vendorCategory: vendorCtg });
                  });
                } else {
                  this.formGroup.patchValue({ vendorCategory: vendorCtg });
                }
              }
            }
          },
        });
    }
    this.autoCompleteCompleted('autoCompleteCode');
  }

  onItemClear(): void {
    this.formGroup.patchValue({ itemDesc: '', brand: null, vendorCategory: null });
    this.vendorCategoryOptions = [];
    this.autoCompleteCleared('autoCompleteCode');
  }

  onBrandSelect(): void {
    this.formGroup.patchValue({ vendorCategory: null });
    this.vendorCategoryOptions = [];
    this.autoCompleteCompleted('autoCompleteCode');
  }

  onBrandClear(): void {
    this.formGroup.patchValue({ vendorCategory: null });
    this.vendorCategoryOptions = [];
    this.autoCompleteCleared('autoCompleteCode');
  }

  onVendorCategoryShow(): void {
    const brandValue = this.formControl('brand').value;
    const brand: string = brandValue?.value ?? '';
    if (!brand) {
      this.systemMessageService.warn(this.translateService.instant('message.detail.need-brand-first'));
      return;
    }
    if (this.vendorCategoryOptions.length === 0) {
      this.loadVendorCategories(brand);
    }
  }

  private normalizeVendorCtg(ctg: string): string {
    let result = ctg.replace(/\.{3}/g, '.');
    while (result.endsWith('..')) {
      result = result.slice(0, -2);
    }
    return result;
  }

  private loadVendorCategories(brand: string, afterLoad?: () => void): void {
    this.loadingVendorCtg = true;
    this.irisoService.getVendorCategories(brand)
      .pipe(take(1), takeUntil(this._destroying$))
      .subscribe({
        next: (data) => { this.vendorCategoryOptions = data; this.loadingVendorCtg = false; afterLoad?.(); },
        error: () => { this.vendorCategoryOptions = []; this.loadingVendorCtg = false; },
      });
  }

  override searchReset(): void {
    this.totalQty = 0;
    this.queryId = '';
    this.vendorCategoryOptions = [];
    super.searchReset();
  }

  // --- Unused overrides ---
  override afterSelectedRowDataCountChange(selectedDataCount: number): void { return; }
  override afterRowDataCountChange(rowDataCount: number): void { return; }
  override initExtraDetailMenu(): void { return; }
  override setExtraDetailMenuStatus(): void { return; }
  override initDialogConfig(dataAction: DataAction): void { return; }
  override initExtraRowActionMenu(): void { return; }
  override setExtraRowActionMenuStatus(): void { return; }
  override doExtraDetailDialogClose(returnData: DialogReturnData): void { return; }
  override async doRowActionDelete(): Promise<boolean> { return true; }
  override afterDetailTabActiveItemChange(): void { return; }
  override initStaticOptions(): void { return; }
  override afterLangChange(): void { return; }
  override afterFormDataLoadFail(): void { return; }
  override initFormControlDisabled(): void { return; }
}
